import { useBreakpointValue } from '@chakra-ui/react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { useSharedPageContext } from '../../contexts/SharedPageContext';
import { SharedPage } from '../SharedPage/SharedPage';
import { AgentComplianceReviewDesktop } from './AgentComplianceReviewDesktop';
import { AgentComplianceReviewMobile } from './AgentComplianceReviewMobile';
import { useAgentComplianceReviewOverview } from './hooks/useAgentComplianceReviewOverview';

/**
 * Shell for the public, no-login agent compliance review page. Loads the
 * overview once the share-link token is verified, then delegates rendering to
 * the layout for the current breakpoint. When a document is selected (the
 * `?documentId=` query param) the selected id flows down and the layout lazily
 * loads that document's detail.
 *
 * Kept intentionally thin: data/verification gating + the desktop/mobile switch
 * live here; the three-pane assembly lives in {@link AgentComplianceReviewDesktop}
 * and the single-pane flow in {@link AgentComplianceReviewMobile}. The `xl`
 * breakpoint matches where the desktop right rail appears.
 */
const AgentComplianceReviewContent = () => {
  const { isShareLinkVerified } = useSharedPageContext();
  const [searchParams] = useSearchParams();
  // Normalize a missing OR empty `?documentId=` to "no selection". An empty
  // string reads as a selection downstream (`!= null`) but the detail query
  // skips on the empty id, which would otherwise strand the page in a detail
  // state with no document loaded.
  const rawSelectedDocumentId = searchParams.get('documentId');
  const selectedDocumentId =
    rawSelectedDocumentId != null && rawSelectedDocumentId.trim() !== ''
      ? rawSelectedDocumentId
      : undefined;

  // Default to desktop until the breakpoint resolves (undefined on first paint)
  // so the wider layout doesn't flash on large screens.
  const isMobile = useBreakpointValue({ base: true, xl: false }) ?? false;

  const { overview, loading, token, ingestionFolderUuid } =
    useAgentComplianceReviewOverview();

  if (!isShareLinkVerified || loading) {
    return <CenterSpinner />;
  }

  if (!overview) {
    return null;
  }

  const Layout = isMobile
    ? AgentComplianceReviewMobile
    : AgentComplianceReviewDesktop;

  return (
    <Layout
      ingestionFolderUuid={ingestionFolderUuid}
      overview={overview}
      selectedDocumentId={selectedDocumentId}
      token={token}
    />
  );
};

/**
 * Route-level wrapper. Mounts {@link SharedPage} once (binding verification to
 * the folder) and renders the review content inside it, so the content inherits
 * SharedPage's verification + expired-link handling. The open document is view
 * state in the query string (`?documentId=`), so there are no child routes.
 */
export const AgentComplianceReviewPage = () => {
  const { ingestionFolderUuid } = useParams();

  return (
    <SharedPage ingestionFolderUuid={ingestionFolderUuid}>
      <AgentComplianceReviewContent />
    </SharedPage>
  );
};
